import React, { useEffect } from "react";
import { useLocation } from "react-router";

import { t } from "i18next";

import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import { Box, Chip, useMediaQuery } from "@mui/material";

import { dealerActiveAuctionSearchFiltersAtom } from "@/src/state/dealer/dealerActiveAuctionSearchFiltersAtom";
import { dealerActiveVehicleFilterAtom } from "@/src/state/dealer/dealerActiveVehicleFilterAtom";
import { showHiddenAuctionsAtom } from "@/src/state/dealer/showHiddenAuctionsAtom";
import { openSearchFilterAtom } from "@/src/state/openSearchFilterAtom";
import { activeAuctionsSortOrderAtom } from "@/src/state/sortOrderAtom";

import CaravanIcon from "@/src/components/atoms/CaravanIcon";
import {
  getModelYearChipText,
  handleVehicleTypeToggle
} from "@/src/components/organisms/SearchFilterDrawer/SearchFilterDrawer.lib";

import PowerTypeQuickFilters from "@/src/pages/dealer/components/PowerTypeQuickFilters/PowerTypeQuickFilters";
import {
  areFiltersSet,
  countActiveFilters
} from "@/src/pages/dealer/components/SearchFilterAndSortPanel/SearchFilterAndSortPanel";
import SearchFilterButton from "@/src/pages/dealer/components/SearchFilterButton/SearchFilterButton";
import ShowHiddenAuctionsButton from "@/src/pages/dealer/components/ShowHiddenAuctionsButton/ShowHiddenAuctionsButton";
import SortAuctionsButton from "@/src/pages/dealer/components/SortAuctionsButton/SortAuctionsButton";
import VehicleCheckBox from "@/src/pages/dealer/components/VehicleCheckBox/VehicleCheckBox";

import { useAtom } from "jotai";

export enum VehicleType {
  CAR = "CAR",
  MOTO = "MOTO",
  CARAVAN = "CARAVAN",
  ALL = "ALL"
}

const filterVehicleTypes = [
  VehicleType.CAR,
  VehicleType.MOTO,
  VehicleType.CARAVAN
];

interface VehicleCheckBoxGroupProps {
  hiddenAuctionsCount?: number;
  showHiddenAuctionsButton?: boolean;
}

const VehicleCheckBoxGroup = ({
  hiddenAuctionsCount = 0,
  showHiddenAuctionsButton = false
}: VehicleCheckBoxGroupProps) => {
  const location = useLocation();
  const [selectedVehicles, setSelectedVehicles] = useAtom<VehicleType[]>(
    dealerActiveVehicleFilterAtom
  );
  const [searchFilters, setSearchFilters] = useAtom(
    dealerActiveAuctionSearchFiltersAtom
  );
  const [searchFilterOpen, setSearchFilterOpen] = useAtom(openSearchFilterAtom);
  const [showHiddenAuctions, setShowHiddenAuctions] = useAtom(
    showHiddenAuctionsAtom
  );
  const isMobile = useMediaQuery("(max-width:700px)");

  const isActiveAuctionsPage = location.pathname === "/dealer/all-active";
  const hasHiddenAuctions = hiddenAuctionsCount > 0;

  const handleVehicleSelection = (vehicleType: VehicleType) => {
    const newVehicles = handleVehicleTypeToggle(vehicleType, selectedVehicles);

    if (newVehicles === selectedVehicles) return;

    // Update both atoms
    setSelectedVehicles(newVehicles);
    setSearchFilters((prev) => ({
      ...prev,
      vehicleType: newVehicles,
      // Reset make and fuelType when vehicle type changes since available options will change
      make: [],
      fuelType: []
    }));
  };

  // Ensure the two states are synchronized on component mount
  useEffect(() => {
    const arraysEqual = (a: VehicleType[], b: VehicleType[]): boolean =>
      a.length === b.length && a.every((v, i) => v === b[i]);

    const storedVehicleType = searchFilters?.vehicleType;
    // Treat empty array same as "not set" — an empty selection is invalid and
    // causes no auctions to show. Fall back to all vehicle types in that case.
    const hasValidVehicleType =
      storedVehicleType && storedVehicleType.length > 0;

    if (hasValidVehicleType) {
      if (!arraysEqual(storedVehicleType, selectedVehicles)) {
        setSelectedVehicles(storedVehicleType);
      }
    } else {
      // searchFilters has no valid vehicleType — either it's missing, undefined,
      // or an empty array. Reset both atoms to all vehicle types.
      const allTypes = filterVehicleTypes;
      if (!arraysEqual(allTypes, selectedVehicles)) {
        setSelectedVehicles(allTypes);
      }
      setSearchFilters((prev) => ({
        ...prev,
        vehicleType: allTypes,
        make: [],
        fuelType: []
      }));
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: ["column", "row"],
        flexWrap: "wrap",
        gap: [1.5, 2],
        ml: [0, 2],
        mr: [0, 2],
        mt: [1, 0],
        mb: [1, 0],
        width: "100%"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: [1, 1.5],
          width: ["100%", "auto"]
        }}
      >
        <VehicleCheckBox
          label={t("dealer.searchFilters.vehicleType.all")}
          value={VehicleType.ALL}
          isChecked={filterVehicleTypes.every((vehicleType) =>
            selectedVehicles.includes(vehicleType)
          )}
          onToggle={handleVehicleSelection}
        />
        <VehicleCheckBox
          label={t("dealer.searchFilters.vehicleType.car")}
          icon={<DirectionsCarIcon />}
          value={VehicleType.CAR}
          isChecked={
            selectedVehicles.length === 1 &&
            selectedVehicles.includes(VehicleType.CAR)
          }
          onToggle={handleVehicleSelection}
          hideLabelOnMobile
        />
        <VehicleCheckBox
          label={t("dealer.searchFilters.vehicleType.moto")}
          icon={<TwoWheelerIcon />}
          value={VehicleType.MOTO}
          isChecked={
            selectedVehicles.length === 1 &&
            selectedVehicles.includes(VehicleType.MOTO)
          }
          onToggle={handleVehicleSelection}
          hideLabelOnMobile
        />
        <VehicleCheckBox
          label={t("dealer.searchFilters.vehicleType.caravan")}
          icon={<CaravanIcon />}
          value={VehicleType.CARAVAN}
          isChecked={
            selectedVehicles.length === 1 &&
            selectedVehicles.includes(VehicleType.CARAVAN)
          }
          onToggle={handleVehicleSelection}
          hideLabelOnMobile
        />
      </Box>

      {isActiveAuctionsPage && (
        <PowerTypeQuickFilters selectedVehicles={selectedVehicles} />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: ["column", "row"],
          flexWrap: "wrap",
          gap: [1, 2],
          ml: [0, "auto"],
          mt: [1, 0],
          width: ["100%", "auto"]
        }}
      >
        {location.pathname === "/dealer/all-active" && (
          <SearchFilterButton
            isOpen={searchFilterOpen}
            activeFilterCount={countActiveFilters(searchFilters)}
            onToggle={() => setSearchFilterOpen(!searchFilterOpen)}
          />
        )}
        {hasHiddenAuctions && showHiddenAuctionsButton && (
          <ShowHiddenAuctionsButton
            showHiddenAuctions={showHiddenAuctions}
            onToggle={() => setShowHiddenAuctions(!showHiddenAuctions)}
          />
        )}

        <Box sx={{ width: ["100%", "auto"] }}>
          <SortAuctionsButton sortOrderAtom={activeAuctionsSortOrderAtom} />
        </Box>
      </Box>

      {isActiveAuctionsPage &&
        areFiltersSet(searchFilters) &&
        (() => {
          const allChips: { label: string; onDelete: () => void }[] = [];

          if ((searchFilters?.distance ?? 0) > 0)
            allChips.push({
              label: `Sijainti: ${searchFilters!.distance} km`,
              onDelete: () =>
                setSearchFilters((prev) => {
                  const {
                    distance: _d,
                    sellerDelivers: _s,
                    ...rest
                  } = prev ?? {};
                  return rest;
                })
            });

          if (searchFilters?.modelYear?.start || searchFilters?.modelYear?.end)
            allChips.push({
              label: `Vuosimalli: ${getModelYearChipText(searchFilters.modelYear)?.[0] ?? ""}`,
              onDelete: () =>
                setSearchFilters((prev) => ({ ...prev, modelYear: undefined }))
            });

          if (searchFilters?.mileage?.start || searchFilters?.mileage?.end)
            allChips.push({
              label: `Kilometrit: ${searchFilters!.mileage!.start ?? 0}–${searchFilters!.mileage!.end ?? "max"} km`,
              onDelete: () =>
                setSearchFilters((prev) => ({ ...prev, mileage: undefined }))
            });

          if ((searchFilters?.make?.length ?? 0) > 0)
            allChips.push({
              label: `Merkki: ${searchFilters!.make!.join(", ")}`,
              onDelete: () =>
                setSearchFilters((prev) => ({ ...prev, make: [] }))
            });

          if ((searchFilters?.fuelType?.length ?? 0) > 0)
            allChips.push({
              label: `Käyttövoima (${searchFilters!.fuelType!.length})`,
              onDelete: () =>
                setSearchFilters((prev) => ({ ...prev, fuelType: [] }))
            });

          if ((searchFilters?.gearType?.length ?? 0) > 0)
            allChips.push({
              label: `Vaihteisto: ${searchFilters!.gearType!.join(", ")}`,
              onDelete: () =>
                setSearchFilters((prev) => ({ ...prev, gearType: [] }))
            });

          if (
            searchFilters?.targetPrice?.start ||
            searchFilters?.targetPrice?.end
          )
            allChips.push({
              label: `Hinta: ${searchFilters!.targetPrice!.start ?? 0}–${searchFilters!.targetPrice!.end ?? "max"} €`,
              onDelete: () =>
                setSearchFilters((prev) => ({
                  ...prev,
                  targetPrice: undefined
                }))
            });

          const MOBILE_MAX = 2;
          const visibleChips =
            isMobile && allChips.length > MOBILE_MAX
              ? allChips.slice(0, MOBILE_MAX)
              : allChips;
          const hiddenCount = allChips.length - visibleChips.length;

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                width: "100%",
                pb: 1
              }}
            >
              {visibleChips.map((chip) => (
                <Chip
                  key={chip.label}
                  size="small"
                  label={chip.label}
                  onClick={() => setSearchFilterOpen(true)}
                  onDelete={chip.onDelete}
                />
              ))}
              {hiddenCount > 0 && (
                <Chip
                  size="small"
                  variant="filled"
                  label={t("dealer.searchFilterGeneral.moreFilters", {
                    count: hiddenCount
                  })}
                  onClick={() => setSearchFilterOpen(true)}
                />
              )}
            </Box>
          );
        })()}
    </Box>
  );
};

export default VehicleCheckBoxGroup;
